import { BASE_ROLE_FLAGS, customerPermissionFlags, ROLE_HIERARCHY } from "../constants/systemPermissions.constants";
import { store } from "../store";

type IntakeUser = { pomMaster?: boolean; permissionFlags?: Record<string, string> } | null | undefined;

/**
 * Can this user see/run Intake Risk Assessments? True for POM master and
 * admins/super admins (implicit access), and for anyone carrying the additive
 * INTAKE_RISK_ASSESSMENT flag. Single source of truth for the IRA role gate.
 * Defaults to the logged-in user when no argument is passed.
 */
export const hasIntakeAccess = (user?: IntakeUser) => {
    const authUser = user === undefined ? store.getState().auth.authUser : user;
    if (!authUser) {
        return false;
    }
    if (authUser.pomMaster) {
        return true;
    }
    const flags = Object.keys(authUser.permissionFlags || {});
    if (flags.includes(customerPermissionFlags.ADMIN) || flags.includes(customerPermissionFlags.SUPER_ADMIN)) {
        return true;
    }
    return flags.includes(customerPermissionFlags.INTAKE_RISK_ASSESSMENT);
}

/**
 * Can this user use IRA All Searches and Report to POM Support (868mkwmk5)?
 * Mirrors pom-2-api's resolveCaller: POM staff (masquerading or POM master)
 * or anyone with intake access. The org setting is checked separately.
 */
export const canUseIraSearchHistory = (user?: (NonNullable<IntakeUser> & { masquarade?: boolean }) | null) => {
    const authUser = user === undefined ? store.getState().auth.authUser : user;
    return !!authUser && (!!authUser.masquarade || hasIntakeAccess(authUser));
}

/**
 * True only when intake is the user's SOLE access — the flag is present and no
 * base role is. These users get the stripped IRA-only experience (Mode 2).
 * POM master and admins are never intake-only (they have full access).
 */
export const isIntakeOnlyUser = (user?: IntakeUser) => {
    const authUser = user === undefined ? store.getState().auth.authUser : user;
    if (!authUser || authUser.pomMaster) {
        return false;
    }
    const flags = Object.keys(authUser.permissionFlags || {});
    const hasIntake = flags.includes(customerPermissionFlags.INTAKE_RISK_ASSESSMENT);
    const hasBaseRole = flags.some((flag) => BASE_ROLE_FLAGS.includes(flag));
    return hasIntake && !hasBaseRole;
}

export const permissionCheck = (requiredFlag: string) => {
    // Get user
    const authUser = store.getState().auth.authUser;
    if (!authUser) {
        return false;
    }

    // Get flags
    const userFlags = Object.keys(authUser.permissionFlags || {});

    // Pom Master check
    if (authUser.pomMaster) {
        return true;
    }

    // Get the highest role the user has (in case they have multiple)
    let userHighestLevel = 0;
    for (const role of userFlags) {
        const level = ROLE_HIERARCHY[role];
        if (level && level > userHighestLevel) {
            userHighestLevel = level;
        }
    }

    // Get required level
    const requiredLevel = ROLE_HIERARCHY[requiredFlag];

    // Allow access if user level >= required level
    return userHighestLevel >= requiredLevel;
}

export const createSystemUserPermissionCheck = (requiredFlag: string) => {
    const authUser = store.getState().auth.authUser;
    const userFlags = Object.keys(authUser.permissionFlags || {});
    const superAdmin = userFlags.includes(customerPermissionFlags.SUPER_ADMIN);
    const admin = userFlags.includes(customerPermissionFlags.ADMIN);
    const viewOnly = userFlags.includes(customerPermissionFlags.VIEW_ONLY);
    const groupLeader = userFlags.includes(customerPermissionFlags.GROUP_LEADER);
    const teamLeader = userFlags.includes(customerPermissionFlags.TEAM_LEADER);

    if (viewOnly) {
        return false;
    }

    if (authUser.pomMaster) {
        return true;
    }

    switch (requiredFlag) {
        case customerPermissionFlags.SUPER_ADMIN:
            return superAdmin;
        case customerPermissionFlags.ADMIN:
        case customerPermissionFlags.VIEW_ONLY:
            return superAdmin || admin;
        case customerPermissionFlags.GROUP_LEADER:
            return superAdmin || admin || groupLeader;
        case customerPermissionFlags.TEAM_LEADER:
            return superAdmin || admin || groupLeader || teamLeader;
        // Granting the Intake Risk Assessment capability (as the Intake
        // Specialist role or the additive toggle) is an admin-level action.
        case customerPermissionFlags.INTAKE_RISK_ASSESSMENT:
            return superAdmin || admin;
        default:
            return false;
    }
}

export const returnRoleFromPermissions = (flags: Record<string, string>) => {
    const keys = Object.keys(flags || {});
    // A real base role always wins (a user with a role + the intake capability
    // is that role, with intake as an add-on — Mode 1).
    const baseRole = BASE_ROLE_FLAGS.find((role) => keys.includes(role));
    if (baseRole) {
        return baseRole;
    }
    // Sole access is intake -> the Intake Specialist role (Mode 2).
    if (keys.includes(customerPermissionFlags.INTAKE_RISK_ASSESSMENT)) {
        return customerPermissionFlags.INTAKE_RISK_ASSESSMENT;
    }
    return 'CUSTOM';
}

// Replaces the old free-text returnCustomPermissions (an imprecise "anything
// that isn't ADMIN/CUSTOM/TEAM_LEADER" filter). The only additive capability
// the product actually has is the Intake Risk Assessment add-on, so this is a
// precise boolean: the target has it AND it's layered on top of a base role,
// not the target's sole/base role already (that's Intake Specialist, not the
// add-on — see returnRoleFromPermissions).
export const hasIntakeAddon = (flags: Record<string, string>) => {
    const keys = Object.keys(flags || {});
    return keys.includes(customerPermissionFlags.INTAKE_RISK_ASSESSMENT)
        && BASE_ROLE_FLAGS.some((role) => keys.includes(role));
}
